import { css, CSSObject } from '@emotion/css';

import { mqBreakpoints, pxToRem } from '../..';

const baseButtonStyle: CSSObject = {
  border: 'none',
  cursor: 'pointer',
  display: 'inline-block',
  fontFamily: 'var(--font-heading)',
  fontWeight: 'var(--font-weight-medium)',
  fontSize: pxToRem(16),
  lineHeight: pxToRem(24),
  marginBottom: pxToRem(48),
  padding: `${pxToRem(12)} ${pxToRem(24)}`,

  [mqBreakpoints.tablet]: {
    fontSize: pxToRem(18),
    marginBottom: pxToRem(64),
    padding: `${pxToRem(20)} ${pxToRem(32)}`,
  },

  /**
   * Colour set by defining these css variables
   *
   * --btn-bg-color
   * --btn-border-color
   * --btn-color
   * --btn-bg-color-hover
   * --btn-border-color-hover
   * --btn-color-hover
   * --btn-bg-color-active
   * --btn-border-color-active
   * --btn-color-active
   */
  backgroundColor: 'var(--btn-bg-color)',
  borderColor: 'var(--btn-border-color)',
  color: 'var(--btn-color)',

  '&:hover': {
    backgroundColor: 'var(--btn-bg-color-hover)',
    borderColor: 'var(--btn-border-color-hover)',
    color: 'var(--btn-color-hover)',
  },

  '&:focus': {
    backgroundColor: 'var(--btn-bg-color-hover)',
    borderColor: 'var(--btn-border-color-hover)',
    color: 'var(--btn-color-hover)',
  },

  '&:active': {
    backgroundColor: 'var(--btn-bg-color-active)',
    borderColor: 'var(--btn-border-color-active)',
    color: 'var(--btn-color-active)',
  },

  '&:disabled': {
    backgroundColor: 'var(--btn-bg-color)',
    borderColor: 'var(--btn-border-color)',
    color: 'var(--btn-color)',
    cursor: 'auto',
    opacity: 0.4,
  },
};

const borderedButtonStyle: CSSObject = {
  borderStyle: 'solid',
  borderWidth: '1px',
  padding: `${pxToRem(11)} ${pxToRem(23)}`,

  [mqBreakpoints.tablet]: {
    padding: `${pxToRem(19)} ${pxToRem(31)}`,
  },
};

const iconButtonStyle: CSSObject = {
  '&.icon': {
    display: 'flex',

    '& > *:first-child': {
      color: 'currentColor',
      marginLeft: pxToRem(-12),
      marginRight: pxToRem(12),
      width: pxToRem(16),

      [mqBreakpoints.tablet]: {
        marginLeft: pxToRem(-16),
        marginRight: pxToRem(16),
        width: pxToRem(24),
      },
    },
  },

  '&.icon-right': {
    display: 'flex',

    '& > *:last-child': {
      color: 'currentColor',
      marginLeft: pxToRem(12),
      marginRight: pxToRem(-12),
      width: pxToRem(16),

      [mqBreakpoints.tablet]: {
        marginLeft: pxToRem(16),
        marginRight: pxToRem(-16),
        width: pxToRem(24),
      },
    },
  },
};

export const primaryButton = css(baseButtonStyle, iconButtonStyle, {
  '--btn-bg-color': 'var(--color-primary)',
  '--btn-border-color': 'var(--color-primary)',
  '--btn-color': 'var(--color-white)',
  '--btn-bg-color-hover': 'var(--color-primary-light)',
  '--btn-border-color-hover': 'var(--color-primary-light)',
  '--btn-color-hover': 'var(--color-white)',
  '--btn-bg-color-active': 'var(--color-primary-dark)',
  '--btn-border-color-active': 'var(--color-primary-dark)',
  '--btn-color-active': 'var(--color-white)',

  '&.bg-dark': {
    '--btn-bg-color': 'var(--color-white)',
    '--btn-border-color': 'var(--color-white)',
    '--btn-color': 'var(--color-primary)',
    '--btn-bg-color-hover': 'var(--color-light)',
    '--btn-border-color-hover': 'var(--color-light)',
    '--btn-color-hover': 'var(--color-primary-dark)',
    '--btn-bg-color-active': 'var(--color-border)',
    '--btn-border-color-active': 'var(--color-border)',
    '--btn-color-active': 'var(--color-body)',
  },
});

export const secondaryButton = css(baseButtonStyle, borderedButtonStyle, iconButtonStyle, {
  '--btn-bg-color': 'transparent',
  '--btn-border-color': 'var(--color-primary)',
  '--btn-color': 'var(--color-primary)',
  '--btn-bg-color-hover': 'var(--color-primary)',
  '--btn-border-color-hover': 'var(--color-primary)',
  '--btn-color-hover': 'var(--color-white)',
  '--btn-bg-color-active': 'var(--color-primary-dark)',
  '--btn-border-color-active': 'var(--color-primary-dark)',
  '--btn-color-active': 'var(--color-white)',

  '&.bg-dark': {
    '--btn-border-color': 'var(--color-white)',
    '--btn-color': 'var(--color-white)',
    '--btn-bg-color-hover': 'var(--color-white)',
    '--btn-border-color-hover': 'var(--color-white)',
    '--btn-color-hover': 'var(--color-body)',
    '--btn-bg-color-active': 'var(--color-border)',
    '--btn-border-color-active': 'var(--color-border)',
    '--btn-color-active': 'var(--color-body)',
  },
});

export const tertiaryButton = css(baseButtonStyle, borderedButtonStyle, iconButtonStyle, {
  '--btn-bg-color': 'transparent',
  '--btn-border-color': 'var(--color-muted)',
  '--btn-color': 'var(--color-muted)',
  '--btn-bg-color-hover': 'var(--color-muted)',
  '--btn-border-color-hover': 'var(--color-muted)',
  '--btn-color-hover': 'var(--color-white)',
  '--btn-bg-color-active': 'var(--color-dark-2-light)',
  '--btn-border-color-active': 'var(--color-dark-2-light)',
  '--btn-color-active': 'var(--color-white)',

  '&.bg-dark': {
    '--btn-border-color': 'var(--color-border)',
    '--btn-color': 'var(--color-border)',
    '--btn-bg-color-hover': 'var(--color-light)',
    '--btn-border-color-hover': 'var(--color-light)',
    '--btn-color-hover': 'var(--color-body)',
    '--btn-bg-color-active': 'var(--color-border)',
    '--btn-border-color-active': 'var(--color-border)',
    '--btn-color-active': 'var(--color-body)',
  },
});

const linkButtonStyle: CSSObject = {
  borderBottomStyle: 'solid',
  borderBottomWidth: '1px',
  boxShadow: 'none',
  marginBottom: pxToRem(32),
  padding: 0,
  paddingBottom: pxToRem(4),
  textDecoration: 'none',

  [mqBreakpoints.tablet]: {
    // override base button tablet style
    fontSize: pxToRem(16),
    marginBottom: pxToRem(32),
    padding: 0,
    paddingBottom: pxToRem(4),
  },

  '&:hover': {
    boxShadow: '0 1px var(--btn-border-color-hover)',
  },

  '&:focus': {
    boxShadow: '0 1px var(--btn-border-color-hover)',
  },

  '&:active': {
    boxShadow: '0 1px var(--btn-border-color-active)',
  },

  '&:disabled': {
    boxShadow: 'none',
  },
};

const iconLinkButtonStyle: CSSObject = {
  '&.icon': {
    display: 'flex',
    marginLeft: pxToRem(24),

    '& > *:first-child': {
      color: 'currentColor',
      marginLeft: pxToRem(-24),
      marginRight: pxToRem(8),
      width: pxToRem(16),

      [mqBreakpoints.tablet]: {
        marginLeft: pxToRem(-32),
        width: pxToRem(24),
      },
    },

    [mqBreakpoints.tablet]: {
      marginLeft: pxToRem(32),
    },
  },

  '&.icon-right': {
    display: 'flex',
    marginRight: pxToRem(24),

    '& > *:last-child': {
      color: 'currentColor',
      marginLeft: pxToRem(8),
      marginRight: pxToRem(-24),
      width: pxToRem(16),

      [mqBreakpoints.tablet]: {
        marginRight: pxToRem(-32),
        width: pxToRem(24),
      },
    },

    [mqBreakpoints.tablet]: {
      marginRight: pxToRem(32),
    },
  },
};

export const linkButton = css(baseButtonStyle, linkButtonStyle, iconLinkButtonStyle, {
  '--btn-bg-color': 'transparent',
  '--btn-border-color': 'var(--color-body)',
  '--btn-color': 'var(--color-body)',
  '--btn-bg-color-hover': 'transparent',
  '--btn-border-color-hover': 'var(--color-primary)',
  '--btn-color-hover': 'var(--color-primary)',
  '--btn-bg-color-active': 'transparent',
  '--btn-border-color-active': 'var(--color-primary-dark)',
  '--btn-color-active': 'var(--color-primary-dark)',

  '&.bg-dark': {
    '--btn-bg-color': 'transparent',
    '--btn-border-color': 'var(--color-white)',
    '--btn-color': 'var(--color-white)',
    '--btn-bg-color-hover': 'transparent',
    '--btn-border-color-hover': 'var(--color-white)',
    '--btn-color-hover': 'var(--color-white)',
    '--btn-bg-color-active': 'transparent',
    '--btn-border-color-active': 'var(--color-border)',
    '--btn-color-active': 'var(--color-border)',
  },
});
